import { useCallback } from "react";

import mapboxgl from "mapbox-gl";
import { useMapStore } from "stores/map";
import { MapInteraction } from "types";

import { useNetworkBoundsManager } from "../toolbar/midstream-navigator/hooks/useNetworkBoundsManager";
import { useGetWellNetworkMap } from "./useGetWellNetworkMap";
import useMutateFacilitiesEnvelope from "./useMutateFacilitiesEnvelope";

export function useNetworkPanToBounds(mapbox: mapboxgl.Map) {
  const { mutateAsync: getFacilitiesEnvelope } = useMutateFacilitiesEnvelope();
  const { panToFocusAreaBounds } = useNetworkBoundsManager(mapbox);

  const wellNetworkMapRequest = useMapStore((s) => s.wellNetworkMapRequest);
  const showNetworkVisualOptions = useMapStore((s) => s.showNetworkOptions);
  const setNetworkEnvelope = useMapStore((s) => s.setNetworkEnvelope);
  const selectedWells = useMapStore((s) => s.selectedWells);
  const { data: wellNetworkMap } = useGetWellNetworkMap(wellNetworkMapRequest);

  const panToNetworkBounds = useCallback(async () => {
    if (!mapbox || !wellNetworkMap || !showNetworkVisualOptions) {
      return;
    }

    // Get fresh values directly instead of relying on potentially stale state
    const freshCurrentWellIds = Object.keys(selectedWells || {});

    // Check if the network data is available for the currently selected well
    const availableWells = Object.keys(wellNetworkMap.wellFeatureCollections || {});
    const selectedWellWithData = freshCurrentWellIds.find((wellId) =>
      availableWells.includes(wellId)
    );

    const freshGeoJson = selectedWellWithData
      ? wellNetworkMap.wellFeatureCollections[selectedWellWithData]
      : null;

    // Use fresh values and only proceed if we have network data for the selected well
    if (selectedWellWithData && freshGeoJson?.features?.length > 0) {
      // Extract facility IDs using the same logic as FacilityHighlighter
      const facilityIds = new Set<string>();

      //handle first element outside of main loop
      const firstFeature = freshGeoJson.features[0];
      if (firstFeature?.properties?.to) {
        facilityIds.add(firstFeature.properties.to);
      }
      //skip first element since its usually a wells
      freshGeoJson.features.slice(1).forEach((feature) => {
        if (feature.properties) {
          if (feature.properties.to) {
            facilityIds.add(feature.properties.to);
          }

          if (feature.properties.from) {
            facilityIds.add(feature.properties.from);
          }
        }
      });

      const facilityIdArray = Array.from(facilityIds);

      if (facilityIdArray.length > 0) {
        try {
          // Ensure map is ready before making API call
          await new Promise<void>((resolve) => {
            if (mapbox.isStyleLoaded() && mapbox.loaded()) {
              mapbox.once(MapInteraction.Idle, () => resolve());
            } else {
              resolve();
            }
          });

          const envelope = await getFacilitiesEnvelope({
            wellId: selectedWellWithData,
            facilityIds: facilityIdArray
          });
          setNetworkEnvelope(envelope);
          // Note: setNetworkEnvelope is handled by panToFocusAreaBounds
          await panToFocusAreaBounds({
            singlePadding: 50,
            combinedPadding: 75,
            duration: 1200,
            networkEnvelope: envelope
          });
        } catch (error) {
          return;
        }
      }
    }
  }, [
    mapbox,
    wellNetworkMap,
    selectedWells,
    showNetworkVisualOptions,
    getFacilitiesEnvelope,
    panToFocusAreaBounds,
    setNetworkEnvelope
  ]);

  return {
    panToNetworkBounds
  };
}
